import React from 'react'
import {
  EditAuditBatch,
  AuditBatchAPI,
  IInventoryAuditBatchContract,
} from '@getgreenline/homi-shared'
import { Empty, Button, message, Icon, Spin } from 'antd'
import Table, { ColumnProps } from 'antd/lib/table'
import { AntAuditInputNumber } from './AntAuditInputNumber'
import { parseErrorMsg, removeDecimalPoint } from '../../../../../../utilities/helpers'
import { AuditDiscrepancy } from './EditAuditProductRow/AuditDiscrepancy'
import { observer } from 'mobx-react'
import { isNumber, isNil } from 'lodash'
import { AuditStore } from '../EditAuditStore'
import { AuditSyncIndicator } from './AuditSyncIndicator'
import { AuditBatchOptions } from './AuditBatchOptions'
import { AuditModels, AuditProductModels } from '@getgreenline/audits'

interface Props {
  auditStore: AuditStore
  auditProduct: AuditProductModels.EditAuditProduct
  isAuditEditable: boolean
  audit: AuditModels.CreateInventoryAudit
}

interface State {
  loadingLotOptions: boolean
  isUpdating: boolean
  isDeleting: boolean
  isAdding: boolean
  targetBatchId?: number
}

interface BatchTableRecord {
  key: number
  lotNumber: string
  counted: JSX.Element
  expected?: JSX.Element
  diffQuantity?: JSX.Element
  options?: JSX.Element
}

@observer
export class AuditBatchTable extends React.Component<Props, State> {
  MESSAGE_KEY = 'LOT_SYNC'

  constructor(props: Props) {
    super(props)

    this.state = {
      loadingLotOptions: false,
      isUpdating: false,
      isDeleting: false,
      isAdding: false,
      targetBatchId: undefined,
    }
  }

  componentDidMount = async () => {
    const { auditProduct, auditStore } = this.props

    const availableBatchOptions = auditStore.getAllBatchOptionsForProduct(auditProduct.productId)

    if (availableBatchOptions !== undefined) {
      return
    }

    this.setState({ loadingLotOptions: true })
    try {
      await auditStore.setAvailableBatchOptions(auditProduct.productId)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading lot options')
    } finally {
      this.setState({ loadingLotOptions: false })
    }
  }

  removeAuditBatch = async (auditBatch: EditAuditBatch) => {
    const { auditProduct, audit, auditStore } = this.props
    const { id, batchNumber } = auditBatch

    this.setState({ isDeleting: true, targetBatchId: auditBatch.id })

    try {
      await AuditBatchAPI.deleteAuditBatch(audit.companyId, audit.id, id)

      auditProduct.removeBatch(id)

      auditStore.deleteAuditBatchSyncCache(auditBatch.id)

      message.success(
        <>
          Lot <strong>{auditBatch.batchNumber}</strong> was successfully removed
        </>,
      )
    } catch (error) {
      message.error(parseErrorMsg(error) || `An error occurred while deleting ${batchNumber}`)
    } finally {
      this.setState({ isDeleting: false, targetBatchId: undefined })
    }
  }

  locallySaveEnteredQuantity = (value: number | undefined, auditBatch: EditAuditBatch) => {
    const locallyEnteredQuantity = isNumber(value) ? value : null

    auditBatch.locallySaveEnteredQuantity(locallyEnteredQuantity)

    const existingCache = this.props.auditStore.getAuditBatchSyncCache(auditBatch.id)

    const syncAttempted = existingCache ? existingCache.syncAttempted : false

    this.props.auditStore.setAuditBatchSyncCache({
      syncAttempted,
      syncSuccessful: undefined,
      cachedAuditBatchContract: {
        id: auditBatch.id,
        inventoryAuditProductId: this.props.auditProduct.id,
        batchId: auditBatch.batchId,
        expectedQuantity: auditBatch.expectedQuantity,
        enteredQuantity: locallyEnteredQuantity,
      },
    })
  }

  syncEnteredQuantityOnBlur = async (auditBatch: EditAuditBatch) => {
    const auditBatchCache = this.props.auditStore.getAuditBatchSyncCache(auditBatch.id)

    if (!auditBatchCache) {
      return
    }

    if (auditBatch.enteredQuantity === auditBatchCache.cachedAuditBatchContract.enteredQuantity) {
      return
    }

    await this.syncEnteredQuantity(auditBatch, auditBatchCache.cachedAuditBatchContract)
  }

  syncEnteredQuantity = async (
    auditBatch: EditAuditBatch,
    auditBatchContract: IInventoryAuditBatchContract,
  ) => {
    const { audit, auditStore } = this.props

    auditStore.deleteAuditBatchSyncCache(auditBatch.id)

    this.setState({ isUpdating: true, targetBatchId: auditBatch.id })

    try {
      const updatedAuditBatch = await AuditBatchAPI.editAuditBatch(
        audit.companyId,
        audit.id,
        auditBatchContract,
      )

      auditBatch.setEnteredQuantity(updatedAuditBatch.enteredQuantity)

      auditStore.setAuditBatchSyncCache({
        syncAttempted: true,
        syncSuccessful: true,
        cachedAuditBatchContract: updatedAuditBatch,
      })
    } catch (error) {
      auditStore.setAuditBatchSyncCache({
        syncAttempted: true,
        syncSuccessful: false,
        cachedAuditBatchContract: auditBatchContract,
      })

      message.error({
        content:
          parseErrorMsg(error) || `An error occurred while updating ${auditBatch.batchNumber}`,
        key: this.MESSAGE_KEY,
        duration: 15,
      })
    } finally {
      this.setState({ isUpdating: false, targetBatchId: undefined })
    }
  }

  isUpdatingBatch = (auditBatch: EditAuditBatch): boolean => {
    const { isUpdating, targetBatchId } = this.state

    return isUpdating && targetBatchId === auditBatch.id
  }

  isDeletingBatch = (auditBatch: EditAuditBatch): boolean => {
    const { isDeleting, targetBatchId } = this.state

    return isDeleting && targetBatchId === auditBatch.id
  }

  get columns(): ColumnProps<BatchTableRecord>[] {
    let defaultColumns: ColumnProps<BatchTableRecord>[] = [
      {
        title: 'Lot number',
        dataIndex: 'lotNumber',
        key: 'lotNumber',
        className: 'align-middle',
        width: 160,
      },
      {
        title: 'Counted',
        dataIndex: 'counted',
        key: 'counted',
        className: 'align-middle',
        width: '8rem',
        align: 'right',
      },
    ]

    if (this.props.auditStore.shouldDisplayQuantity) {
      defaultColumns = [
        ...defaultColumns,
        {
          title: 'Expected',
          dataIndex: 'expected',
          key: 'expected',
          width: '8rem',
          className: 'align-middle',
          align: 'right',
        },
        {
          title: 'Difference',
          dataIndex: 'diffQuantity',
          key: 'diffQuantity',
          width: '8rem',
          className: 'align-middle',
          align: 'right',
        },
      ]
    }

    if (this.props.isAuditEditable && this.props.auditProduct.batches.length > 0) {
      defaultColumns = [
        ...defaultColumns,
        {
          title: '',
          dataIndex: 'options',
          key: 'options',
          width: '54px',
          className: 'align-middle',
          align: 'center',
        },
      ]
    }

    return defaultColumns
  }

  get tableDataSource(): BatchTableRecord[] {
    return this.props.auditProduct.batches.map((auditBatch: EditAuditBatch) => {
      const { isAuditEditable, auditStore, audit } = this.props
      const { id, batchNumber, locallySavedEnteredQuantity, enteredQuantity, expectedQuantity } =
        auditBatch

      const auditBatchCache = auditStore.getAuditBatchSyncCache(auditBatch.id)

      return {
        key: id,
        lotNumber: batchNumber,
        counted: (
          <div className='d-flex align-items-center'>
            <AntAuditInputNumber
              className='w-100'
              isEditable={isAuditEditable}
              value={locallySavedEnteredQuantity}
              min={0}
              onChange={(value) => this.locallySaveEnteredQuantity(value, auditBatch)}
              onBlur={async () => this.syncEnteredQuantityOnBlur(auditBatch)}
              disabled={this.isUpdatingBatch(auditBatch)}
              formatter={(value) => {
                // Formatter function expects only string values to be returned
                if (value === '' || isNil(value)) {
                  return ''
                }
                return `${isNaN(removeDecimalPoint(value)) ? '' : removeDecimalPoint(value)}`
              }}
            />

            <div
              style={{
                minWidth: 21,
                textAlign: 'right',
              }}
            >
              {auditBatchCache === undefined
                ? this.isUpdatingBatch(auditBatch) && (
                    <Spin indicator={<Icon type='loading' spin />} size='small' />
                  )
                : audit.status === 'pending' && (
                    <AuditSyncIndicator
                      valueChanged={locallySavedEnteredQuantity !== enteredQuantity}
                      syncAttempted={auditBatchCache.syncAttempted}
                      syncSuccessful={auditBatchCache.syncSuccessful}
                      onClick={async () =>
                        this.syncEnteredQuantity(
                          auditBatch,
                          auditBatchCache.cachedAuditBatchContract,
                        )
                      }
                    />
                  )}
            </div>
          </div>
        ),
        expected: (
          <div>
            {enteredQuantity === null || expectedQuantity === null ? '-' : expectedQuantity}
          </div>
        ),
        diffQuantity: (
          <AuditDiscrepancy
            enteredQuantity={locallySavedEnteredQuantity}
            expectedQuantity={expectedQuantity}
          />
        ),
        options: isAuditEditable ? (
          <Button
            icon='close'
            size='small'
            tabIndex={-1}
            loading={this.isDeletingBatch(auditBatch)}
            disabled={this.isDeletingBatch(auditBatch)}
            onClick={() => this.removeAuditBatch(auditBatch)}
          />
        ) : undefined,
      }
    })
  }

  render() {
    const { auditStore, auditProduct } = this.props

    return (
      <>
        <Table
          size='small'
          className={`w-${auditStore.shouldDisplayQuantity ? '50' : '25'} no-background-table`}
          pagination={false}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No lots added yet' />
            ),
          }}
          columns={this.columns}
          dataSource={this.tableDataSource}
          rowClassName={() => 'background-white'}
          loading={this.state.isAdding}
        />

        {this.props.isAuditEditable && (
          <AuditBatchOptions
            auditStore={auditStore}
            auditProduct={auditProduct}
            loading={this.state.loadingLotOptions}
            setIsAdding={(isAdding: boolean) => this.setState({ isAdding })}
          />
        )}
      </>
    )
  }
}
